import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { a as sortByAreaTitle, F as FilterService } from './index2.js';
import { H as HeadlineAppearance, d as defineCustomElement$6 } from './roche-headline2.js';
import { T as TableSortHeadersAppearance, d as defineCustomElement$2 } from './roche-table-sort-header2.js';
import { d as defineCustomElement$5 } from './roche-icon2.js';
import { d as defineCustomElement$4 } from './roche-pipeline-caption2.js';
import { d as defineCustomElement$3 } from './roche-pipeline-diagnostics-row2.js';

// TODO: Use lists of lists instead of objects as a refactor for clearer code.
const diagnosticsData = [];
const FilterDiagnostics = {
    sortElements(content, sortParams) {
        return sortByAreaTitle(content, sortParams);
    },
    groupElements(content, groupParams) {
        return FilterService.groupByMultipleKeys(content, groupParams);
    },
    handleTypes(content) {
        const areaKeys = Object.keys(content);
        areaKeys.forEach((key) => {
            diagnosticsData.push({ isHeadline: true, groupBy: key });
            this.handleAreas(content, key);
        });
        return diagnosticsData;
    },
    handleAreas(content, areaKey) {
        const titleValues = Object.values(content[areaKey]);
        titleValues.forEach((titles) => {
            titles.forEach((element, index) => {
                const { title, productName, description, market, area, color, } = JSON.parse(JSON.stringify(element));
                const temporaryObject = {
                    title,
                    area,
                    productName,
                    description,
                    market,
                    color,
                    isTitleVisible: true,
                    isBorderVisible: false,
                };
                if (titles.length - 1 === index) {
                    temporaryObject.isTitleVisible = !(index > 0);
                    temporaryObject.isBorderVisible = true;
                    return diagnosticsData.push(Object.assign({}, temporaryObject));
                }
                // Whenever multiple areas are available only the first area will have area color
                if (index > 0) {
                    temporaryObject.isTitleVisible = false;
                    return diagnosticsData.push(Object.assign({}, temporaryObject));
                }
                return diagnosticsData.push(Object.assign({}, temporaryObject));
            });
        });
    },
};

const rochePipelineDiagnosticsCss = ":host{display:block;margin-top:3rem;display:block}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}.diagnostics-caption{max-width:18.75rem;margin-top:3rem}@media (min-width: 20rem){.diagnostics-caption{margin-top:2rem}}@media (min-width: 48rem){.diagnostics-caption{margin-top:3rem}}.diagnostics-caption__item .abbreviation{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--caption--font-size--small-up);line-height:1.1875rem;font-weight:400}@media (min-width: 48rem){.diagnostics-caption__item .abbreviation{font-size:var(--caption--font-size--medium-up);line-height:1.375rem}}.diagnostics-caption__item .description{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--caption--font-size--small-up);line-height:1.1875rem}@media (min-width: 48rem){.diagnostics-caption__item .description{font-size:var(--caption--font-size--medium-up);line-height:1.375rem}}";
const RochePipelineDiagnosticsStyle0 = rochePipelineDiagnosticsCss;

const headers = [
    {
        name: 'area',
        key: 'area',
    }, {
        name: 'product name',
        key: 'productName',
    }, {
        name: 'description',
        key: 'description',
    }, {
        name: 'market',
        key: 'market',
    },
];
const headerNames = headers.map((header) => header.name);
const PipelineDiagnosticsCaption = [
    {
        abbreviation: 'CE - ',
        description: 'European Conformity;',
    },
    {
        abbreviation: 'US - ',
        description: 'FDA Approval;',
    },
    {
        abbreviation: 'WW - ',
        description: 'Worldwide including CE, US and China;',
    },
    {
        sup: '1 ',
        abbreviation: 'OUS - ',
        description: 'Outside the US; PCR:  Polymerase Chain Reaction; RT: Real Time;',
    },
    {
        sup: '2 ',
        abbreviation: 'EUA - ',
        description: 'Emergency Use Authorization;',
    },
    {
        sup: '3 ',
        description: 'Only selected countries',
    },
];
const RochePipelineDiagnostics$1 = /*@__PURE__*/ proxyCustomElement(class RochePipelineDiagnostics extends HTMLElement {
    groupData() {
        var _a;
        if (((_a = this.contentParsed) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            const sortedResult = FilterDiagnostics.sortElements(this.contentParsed, ['area', 'title']);
            const groupedResult = FilterDiagnostics.groupElements(sortedResult, ['area', 'title']);
            this.diagnosticsData = FilterDiagnostics.handleTypes(groupedResult);
        }
    }
    handleResize() {
        if (this.isMobile) {
            this.headers = [headerNames[1], headerNames[3]];
        }
        else {
            this.headers = headerNames;
        }
    }
    constructor() {
        super();
        this.__registerHost();
        this.content = undefined;
        this.isMobile = undefined;
        this.headers = headerNames;
        this.diagnosticsData = [];
        this.handleResize = debounce_1.debounce(this.handleResize.bind(this), 250);
    }
    get contentParsed() {
        return typeof this.content === 'string' ? JSON.parse(this.content) : this.content;
    }
    componentWillLoad() {
        this.handleResize();
        this.groupData();
    }
    render() {
        var _a;
        return (h(Host, { key: '2ce368dc739409825b17498d4538f54f6dfa1b3e' }, h("div", { key: 'ebd052f56211d65c91ca228c4cfa57778cb63687', class: "pipeline-table" }, h("roche-table-sort-header", { key: '2044adeef243e3ddfbff6e412e68bfd362b4c91b', appearance: TableSortHeadersAppearance.DIAGNOSTICS, sortable: false, headers: this.headers }), (_a = this.diagnosticsData) === null || _a === void 0 ? void 0 :
            _a.map((item, index) => (item.isHeadline
                ? h("roche-headline", { key: index, level: 6, headline: item.groupBy, appearance: HeadlineAppearance.PIPELINE })
                : h("roche-pipeline-diagnostics-row", { key: `pipeline-entry-${index}`, class: 'table__row', "diagnostic-title": item.title, area: item.area, "product-name": item.productName, description: item.description, market: item.market, color: item.color, "is-title-visible": item.isTitleVisible, "is-border-visible": item.isBorderVisible, "is-mobile": this.isMobile })))), h("roche-pipeline-caption", { key: '1860bfe7e8ee47d83f6de92702ad6d78abce258a', "caption-string": (JSON.stringify(PipelineDiagnosticsCaption)) })));
    }
    static get watchers() { return {
        "content": ["groupData"]
    }; }
    static get style() { return RochePipelineDiagnosticsStyle0; }
}, [0, "roche-pipeline-diagnostics", {
        "content": [1],
        "isMobile": [4, "is-mobile"],
        "headers": [32],
        "diagnosticsData": [32]
    }, [[9, "resize", "handleResize"]], {
        "content": ["groupData"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-pipeline-diagnostics", "roche-headline", "roche-icon", "roche-pipeline-caption", "roche-pipeline-diagnostics-row", "roche-table-sort-header"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-pipeline-diagnostics":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RochePipelineDiagnostics$1);
            }
            break;
        case "roche-headline":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "roche-pipeline-caption":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-pipeline-diagnostics-row":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-table-sort-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RochePipelineDiagnostics = RochePipelineDiagnostics$1;
const defineCustomElement = defineCustomElement$1;

export { RochePipelineDiagnostics, defineCustomElement };

//# sourceMappingURL=roche-pipeline-diagnostics.js.map